/**
 * El modelo de un reporte entregable: qué dice, no cómo se dibuja.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 * Antes había dos cosas distintas con el mismo nombre. La pantalla mostraba
 * tarjetas, gráficos y una tabla paginada; el PDF era un `autoTable` pelado con
 * el título encima. Nadie podía ver antes de descargar lo que iba a descargar,
 * y lo que se descargaba no se parecía a lo que se había revisado.
 *
 * Con un modelo en medio, la previsualización y el PDF son DOS LECTURAS DEL
 * MISMO OBJETO. Si un bloque no está aquí, no sale en ninguno de los dos; si
 * está, sale en los dos y con el mismo contenido. Esa es toda la idea.
 *
 * ── Por qué el modelo es de bloques y no un árbol libre ─────────────────────
 * Un árbol libre acabaría siendo HTML, y entonces el PDF tendría que
 * interpretarlo. Son seis bloques contados, cada uno con una pinta fija: eso es
 * lo que permite que el renderizador de PDF quepa en un archivo y que la
 * pantalla no tenga que adivinar nada.
 */

/** Una celda ya formateada. El modelo no formatea: recibe texto listo. */
export type Celda = string | number | null | undefined;

export interface ColumnaDoc {
  clave: string;
  titulo: string;
  /** Los números van a la derecha y en versalita tabular; el texto a la izquierda. */
  alinea?: "izquierda" | "derecha" | "centro";
  /** Fuerza monoespaciada: para claves, folios e identificadores. */
  mono?: boolean;
}

/** El tono de una nota o de un dato. Son los del tema, no colores sueltos. */
export type Tono = "neutral" | "info" | "exito" | "aviso" | "error";

export interface DatoResumen {
  etiqueta: string;
  valor: string;
  /** Una aclaración de una línea bajo el número («estimado», «de 24 h»). */
  nota?: string;
  tono?: Tono;
}

export interface Campo {
  etiqueta: string;
  valor: string;
  mono?: boolean;
}

/**
 * Un reloj: el aro de un indicador.
 *
 * `fraccion` entre 0 y 1 dibuja un aro parcial — el indicador es PARTE de algo.
 * `null` con valor dibuja el aro completo: es un absoluto sin denominador, y el
 * aro lleno afirma «este número está completo», no «100 %».
 * `null` sin valor dibuja el aro punteado: no se sabe, que no es cero.
 */
export interface Reloj {
  etiqueta: string;
  valor: string | null;
  fraccion: number | null;
  tono?: Tono;
  nota?: string;
}

/** Un tramo de la regla de 24 horas. */
export interface TramoRegla {
  desde: number;
  hasta: number;
  estado: string;
}

export interface DiaRegla {
  etiqueta: string;
  /** Medianoche local del día, en epoch ms: la regla siempre cubre 24 h desde ahí. */
  inicio: number;
  tramos: TramoRegla[];
  /** La línea de cifras que acompaña al día, ya formateada. */
  cifras?: string;
}

export type Bloque =
  | { tipo: "resumen"; datos: DatoResumen[] }
  | { tipo: "relojes"; titulo?: string; relojes: Reloj[]; pie?: string }
  | {
      tipo: "campos";
      titulo?: string;
      campos: Campo[];
      /**
       * Retrato de quien va en la ficha, si lo hay.
       *
       * Es una URL y no un dato ya incrustado: la pantalla la pinta con un
       * `<img>` y el PDF la descarga y la convierte justo antes de imprimir. Si
       * no se puede cargar, el documento sale igual sin ella — una exportación
       * no se cae por una foto.
       */
      foto?: string | null;
    }
  | {
      tipo: "tabla";
      titulo?: string;
      columnas: ColumnaDoc[];
      filas: Record<string, Celda>[];
      pie?: string;
      /** Tope de filas que se imprimen en el PDF. El resto se resume en el pie. */
      topePdf?: number;
    }
  | { tipo: "regla"; titulo?: string; dias: DiaRegla[]; leyenda: { estado: string; etiqueta: string }[]; pie?: string }
  | { tipo: "nota"; titulo?: string; texto: string; tono?: Tono }
  | { tipo: "total"; etiqueta: string; valor: string }
  | { tipo: "salto" };

export interface DocumentoReporte {
  /** Lo que va grande en el encabezado. */
  titulo: string;
  /** La línea bajo el título: folio, rango, lo que identifique esta corrida. */
  folio: string;
  /** Cliente y unidad. */
  subtitulo: string;
  /** La píldora verde del encabezado. Opcional: sin dato que la justifique, no va. */
  sello?: string;
  bloques: Bloque[];
  /**
   * `"factura"` imprime la hoja con el diseño del PDF de facturación (tarjetas,
   * tabla con cabecera navy, píldora de estado y pie con paginación). Sin
   * valor, se conserva el diseño anterior: los reportes migran uno a uno.
   */
  estilo?: "factura";
  /** Nombre del archivo SIN extensión. */
  archivo: string;
}

/** Quita los bloques vacíos: una tabla sin filas es ruido, no una sección. */
export function compactar(bloques: (Bloque | null | false | undefined)[]): Bloque[] {
  const out: Bloque[] = [];
  for (const b of bloques) {
    if (!b) continue;
    if (b.tipo === "tabla" && b.filas.length === 0) continue;
    if (b.tipo === "campos" && b.campos.length === 0) continue;
    if (b.tipo === "relojes" && b.relojes.length === 0) continue;
    if (b.tipo === "regla" && b.dias.length === 0) continue;
    // Dos saltos seguidos, o un salto al principio, no separan nada.
    if (b.tipo === "salto" && (out.length === 0 || out[out.length - 1].tipo === "salto")) continue;
    out.push(b);
  }
  while (out.length && out[out.length - 1].tipo === "salto") out.pop();
  return out;
}
